import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { withRouter } from 'next/router';
import PropTypes from 'prop-types';
import { Component, createContext } from 'react';
import { getHealth, getMe } from '@api/AuthApi';
import DatadogService from '@services/DatadogService';
import LaunchDarklyService from '@services/LaunchDarklyService';
import RedirectService from '@services/RedirectService';
import { Global } from '@utils/common';
import { ConfigurationsContext } from 'components/providers/ConfigurationsProvider/ConfigurationsProvider';

// 10 minutes
// This should always be smaller than the JWT expiration
const JWT_INTERVAL = 600000;

export const UserContext = createContext({
  assigned_business_unit: {
    id: 0,
    name: '',
  },
  business_region: '',
  business_units: [],
  can_impersonate: false,
  category: null,
  clearUserData: () => {},
  company: {
    award_price_precision: 2,
    id: 0,
    name: '',
    wordmark_url: '',
  },
  company_date_part_order: '',
  company_uuid: '',
  date_part_order: '',
  deeplink_contextable_id: '',
  email: '',
  first_name: '',
  // date_time_formats and bidding_events come from the real Global config, not a stub: getDateTimeFormatObject
  // dereferences it unconditionally, and since every format key is optional a partial stub would
  // silently hand dayjs an undefined format and emit ISO. Scoped to date_time_formats rather than
  // spreading all of Global, which concretises unrelated members and shifts type errors in files this
  // change does not touch.
  global: {
    bidding_events: Global.bidding_events,
    date_time_formats: Global.date_time_formats,
    requests: {
      states: {}
    }
  },
  hasAttemptedAuth: false,
  id: 0,
  impersonating: false,
  is_admin: false,
  is_buyer: false,
  is_deeplink: false,
  is_owner: false,
  is_self_serve: false,
  is_super_admin: false,
  is_supplier: false,
  isSuperAdmin: () => false,
  job_title: '',
  last_name: '',
  name: '',
  on_boarding: '',
  one_schema_token: '',
  organization_id: 0,
  organization_name: '',
  phone: '',
  profile_picture_url: undefined,
  provider: '',
  resetMe: () => {},
  role: '',
  setUserProperty: (_any, _callback) => {},
  subdomain: '',
  tag_names: [],
});

class UserProvider extends Component {
  constructor (props) {
    super(props);

    this.state = {
      hasAttemptedAuth: false,
      hasFailedHealthCheck: false
    };
  }

  componentDidMount () {
    this.isSubscribed = true;

    DatadogService.initialize();

    this.resetMe();

    // Periodically refreshing the JWT avoids odd redirects and UI flicker when a user navigates to another page
    // during a stale sessions where the JWT has expired.
    this.interval = setInterval(this.healthCheck, JWT_INTERVAL);

    // Browsers will deprioritize background windows/tabs which could lead to stale access tokens that don't
    // get refreshed before a page navigation happens.  Listening for visibility change and firing
    document.addEventListener('visibilitychange', this.healthCheck);
  }

  componentWillUnmount () {
    this.isSubscribed = false;
    clearInterval(this.interval);

    document.removeEventListener('visibilitychange', this.healthCheck);
  }

  // We are only running a health check here to ensure the JWT is active. We don't
  // care about setting any state from this.
  healthCheck = () => {
    if (document.hidden) return;
    if (RedirectService.isAuthenticationPage(this.props.router)) return;

    // If we've failed a health check, we're not signed in and should not continue to health check.
    // Something to think about.. avoiding a healthcheck retrying when signed out.
    // if (this.state.hasFailedHealthCheck) return;

    getHealth();
    // getHealth().then(results => {
    //   if (results?.response?.data?.errors) this.setState({ hasFailedHealthCheck: true });
    // });
  };

  datePartOrder = user => {
    const { date_part_order: userDatePartOrder, company_date_part_order: companyDatePartOrder } = user;
    const defaultDatePartOrder = this.props.configurations.global.date_time_formats.default_date_part_order;

    return userDatePartOrder || companyDatePartOrder || defaultDatePartOrder;
  };

  resetMe = () => getMe().then(response => {
    if (!this.isSubscribed) return;

    const { router } = this.props;

    if (response.status !== 200) {
      // null indicates not authorized
      this.setState({ hasAttemptedAuth: true });
      return;
    }

    const { data: user } = response;

    this.setState({
      ...{ ...user, date_part_order: this.datePartOrder(user) },
      hasAttemptedAuth: true
    }, () => {
      // If we're logged in and landed on an authentication page, redirect to root.
      // If the user is trying to access a super admin page, redirect.
      // There is a momentary flash of the sign-in page before it redirects. Better way to handle?
      RedirectService.redirectAuthedUser({ router, user });
      // Reconfigure the user here, to handle flagging changes when impersonating a user.
      // ldClient is not guaranteed to exist here on page load, because we don't use the async provider
      // from LD. Because of this, we configure the user also in App.tsx to handle first page load.
      LaunchDarklyService.configureUser(this.props.ldClient, user);
      DatadogService.configureUser(user);
    });
  });

  clearUserData = () => {
    this.setState({});
  };

  isSuperAdmin = () => {
    return Boolean(this.state?.true_user?.category === 'superadmin');
  };

  setUserProperty = (property, callback) => {
    this.setState(property, newState => {
      if (callback) callback(newState);
    });
  };

  render () {
    return (
      <UserContext.Provider
        displayName='UserContext'
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          ...this.state,
          clearUserData: this.clearUserData,
          global: this.props.configurations.global,
          isSuperAdmin: this.isSuperAdmin,
          resetMe: this.resetMe,
          setUserProperty: this.setUserProperty,
        }}
      >
        {this.props.children}
      </UserContext.Provider>
    );
  }
}

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
  configurations: PropTypes.shape().isRequired
};

const exportedComponent = props => (
  <ConfigurationsContext.Consumer>
    {configurations => <UserProvider {...props} configurations={configurations}/>}
  </ConfigurationsContext.Consumer>
);

export default withLDConsumer()(withRouter(exportedComponent));
